import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';

const fadeInOut = keyframes`
  0% { opacity: 0.7; transform: scale(0.95); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.7; transform: scale(0.95); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

const CountdownWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  borderRadius: '50%',
  width: 80,
  height: 80,
  boxShadow: theme.shadows[10]
}));

const CountdownText = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 700,
  fontSize: '1.8rem',
  lineHeight: 1
}));

function FeedbackPanel({
  isFullBodyVisible,
  isTooClose,
  distance,
  distanceStatus,
  countdown,
  initialCountdown,
  posture,
  missingPoints,
  poseChanged,
  attempts,
  setAttempts
}) {
  const feedbackMessage = useMemo(() => {
    const poseName = posture?.name || 'the pose';
    if (countdown !== null) {
      return poseChanged ? 'Pose changed. Please hold the initial pose.' : `Hold ${poseName} for ${countdown} seconds`;
    }
    if (isTooClose) return 'Move back! You are too close to the camera.';
    if (distanceStatus === 'too far') return 'Move closer! You are too far from the camera.';
    return missingPoints.length > 0
      ? 'Some body parts are not visible. Please adjust your position.'
      : 'Please ensure your full body is visible from head to toe.';
  }, [countdown, posture?.name, missingPoints, isTooClose, distanceStatus, poseChanged]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
      <Card
        sx={{ bgcolor: 'white', height: 150, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
        role="region"
        aria-label="Pose detection feedback"
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '12px', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600,
                color: isFullBodyVisible && !isTooClose && distanceStatus === 'good distance' ? '#4caf50' : '#ef5350'
              }}
              id="feedback-title"
            >
              {isFullBodyVisible && !isTooClose && distanceStatus === 'good distance' ? 'Pose Detected' : 'Position Feedback'}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.5 }}
            aria-labelledby="feedback-title"
            aria-live="polite"
          >
            {feedbackMessage}
          </Typography>
        </CardContent>
      </Card>
      {distance !== null && (
        <Card sx={{ bgcolor: 'white', height: { xs: 300, sm: 220 }, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 },
              p: { xs: '12px', sm: '16px' },
              height: '100%',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {(initialCountdown !== null || countdown !== null) && (
              <CountdownWrapper>
                <CircularProgress
                  variant="determinate"
                  value={
                    initialCountdown !== null ? ((7 - initialCountdown) / 7) * 100 : countdown !== null ? ((15 - countdown) / 15) * 100 : 0
                  }
                  size={80}
                  thickness={4}
                  sx={{ color: '#4caf50', position: 'absolute' }}
                />
                <CountdownText>{initialCountdown !== null ? initialCountdown : countdown}</CountdownText>
              </CountdownWrapper>
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, color: '#333' }}>
                Camera Distance
              </Typography>
              <Tooltip title="Adjust distance to stay in the green zone (30-75%)">
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60, my: 1 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      border: '3px solid',
                      borderColor: distance < 30 ? '#ef5350' : distance < 75 ? '#ffb300' : '#4caf50',
                      animation:
                        isFullBodyVisible && !isTooClose && distanceStatus === 'good distance' ? `${pulse} 2s ease-in-out infinite` : 'none'
                    }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={60}
                    thickness={4}
                    sx={{ color: '#e0e0e0', position: 'absolute' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={distance}
                    size={60}
                    thickness={4}
                    sx={{ color: distance < 30 ? '#ef5350' : distance < 75 ? '#ffb300' : '#4caf50', position: 'absolute' }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}
                    aria-label={`Distance from camera: ${Math.round(distance)} percent`}
                  >
                    {Math.round(distance)}%
                  </Typography>
                </Box>
              </Tooltip>
              <Typography
                variant="body2"
                color={distanceStatus === 'too close' ? 'error.main' : distanceStatus === 'too far' ? 'warning.main' : 'success.main'}
                sx={{ textAlign: 'center', fontSize: '0.85rem' }}
              >
                {distanceStatus === 'too close' ? 'Too close' : distanceStatus === 'too far' ? 'Too far' : 'Good distance'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
      {attempts > 0 && (
        <Card sx={{ bgcolor: 'white', height: 80, borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '12px', height: '100%' }}>
            <Typography variant="body2" color="error" sx={{ fontSize: '0.875rem', fontWeight: 500 }} aria-live="assertive">
              Attempt {attempts} of 3
            </Typography>
            {attempts >= 3 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAttempts(0)}
                sx={{ ml: 'auto', fontSize: '0.75rem', py: '4px' }}
                aria-label="Retry pose capture"
              >
                Retry
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default FeedbackPanel;
